import {
    Component,
    ChangeDetectionStrategy,
    Output,
    EventEmitter,
    ChangeDetectorRef,
    Input,
    SimpleChanges,
    HostBinding,
} from '@angular/core';
import { Router } from '@angular/router';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { navigationButtons } from 'layout/navigation-buttons';
import { AccessCondition, AccessString, AuthService, PermissionsHelper } from 'providers/auth.service';
import { EnvironmentService } from 'providers/environment.service';
import { BehaviorSubject, combineLatest } from 'rxjs';
import { filter } from 'rxjs/operators';
import { LicensingApiService } from '@kempoweroyj/ngx-licensing-api-client';

import { FeatureFlagService, DEVELOPMENT_FEATURE_FLAGS } from '../../services/feature-flags';
import { LicenseInfoService } from 'shared/organization-info/license-info.service';
import { OrganizationInfoService } from 'shared/organization-info/organization-info.service';

@Component({
    selector: 'ce-topbar',
    templateUrl: './topbar.component.html',
    styleUrls: ['./topbar.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
@UntilDestroy()
export class TopbarComponent {
    @Input() pageId: string;
    @Output() menuClicked = new EventEmitter<void>();
    @HostBinding('class') public themeClass: 'theme-mykempower' | 'theme-chargeye' = 'theme-chargeye';

    filteredButtons: Partial<typeof navigationButtons> = [];
    organizationName$ = new BehaviorSubject(null);
    licenseName$ = new BehaviorSubject(null);
    isSearchFieldActive$ = new BehaviorSubject(false);
    hasPermissionsToSearch$ = new BehaviorSubject(false);

    featureFlagState: { [key: string]: boolean } = {};
    isDarkMode = false;

    private developmentFlags = DEVELOPMENT_FEATURE_FLAGS;

    sectionName: string;

    showBanner = false;
    daysUntilSubscriptionExpiry: number | null = null;
    isOrganizationAdmin = false;

    private readonly SUBSCRIPTION_BANNER_KEY_PREFIX = 'ce-subscription-banner-last-shown-date';

    constructor(
        private auth: AuthService,
        private featureFlagService: FeatureFlagService,
        private cdRef: ChangeDetectorRef,
        private orgService: OrganizationInfoService,
        private licenseService: LicenseInfoService,
        private licensingApi: LicensingApiService,
        private readonly environmentService: EnvironmentService,
        private readonly router: Router
    ) {
        this.environmentService
            .getAppOrigin$()
            .pipe(untilDestroyed(this))
            .subscribe((origin) => {
                this.themeClass = origin === 'mykempower' ? 'theme-mykempower' : 'theme-chargeye';
                this.cdRef.markForCheck();
            });

        // Subscribe separately to organization and license info to ensure they update immediately
        this.orgService.organizationInfo$
            .pipe(
                filter((info) => !!info),
                untilDestroyed(this)
            )
            .subscribe((organizationInfo) => {
                const orgName =
                    organizationInfo?.impersonated?.tenantName || organizationInfo?.originalTenantName || '';
                this.organizationName$.next(orgName);
                this.cdRef.markForCheck();
            });

        this.licenseService.licenseInfo$
            .pipe(
                filter((info) => !!info),
                untilDestroyed(this)
            )
            .subscribe((license) => {
                const licName = license?.licenseDefinition?.name || license?.licenseDefinition?.type || '';
                this.licenseName$.next(licName);
                this.cdRef.markForCheck();
            });

        combineLatest([
            this.auth.authenticated$,
            this.featureFlagService.getFlagState$(),
            this.auth.permissions$,
            this.orgService.organizationInfo$,
            this.licenseService.licenseInfo$,
        ])
            .pipe(untilDestroyed(this))
            .subscribe(([, flagState, helper, _organizationInfo, _license]) => {
                this.featureFlagState = flagState;
                this.isDarkMode = flagState['enableDarkMode'] || false;
                this.isOrganizationAdmin = this.auth.getPermissions().isOrganizationAdmin || false;

                // Fetch all licenses and check for expiry
                this.checkSubscriptionExpiryWithFollowingLicenses();
                this.refreshButtons(helper);
                this.cdRef.markForCheck();
            });
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes.pageId) {
            if (this.pageId === 'iam') {
                this.sectionName = 'Account administration';
            } else {
                this.sectionName = '';
            }
            this.cdRef.markForCheck();
        }
    }

    navigateToHome(): void {
        if (this.themeClass === 'theme-mykempower') {
            // For MyKempower, always go to stations
            this.router.navigate(['/search/stations']);
        } else {
            // For ChargEye, go to root (which will use default routing)
            this.router.navigate(['/']);
        }
    }

    setSearchActive($event): void {
        this.isSearchFieldActive$.next($event);
    }

    onDismissBanner(): void {
        this.showBanner = false;
        // Store today's date in localStorage with tenant ID
        const today = new Date().toDateString();
        const storageKey = this.getBannerStorageKey();
        if (storageKey) {
            localStorage.setItem(storageKey, today);
        }
        this.cdRef.markForCheck();
    }

    private bannerWasShownToday(): boolean {
        const storageKey = this.getBannerStorageKey();
        if (!storageKey) {
            return false;
        }
        const lastShownDate = localStorage.getItem(storageKey);
        if (!lastShownDate) {
            return false;
        }
        const today = new Date().toDateString();
        return lastShownDate === today;
    }

    private getBannerStorageKey(): string | null {
        const tenantId = this.auth.profile?.tenantId;
        if (!tenantId) {
            return null;
        }
        return `${this.SUBSCRIPTION_BANNER_KEY_PREFIX}-${tenantId}`;
    }

    private async checkSubscriptionExpiryWithFollowingLicenses(): Promise<void> {
        // Reset banner state
        this.showBanner = false;
        this.daysUntilSubscriptionExpiry = null;

        // Check basic conditions that would prevent showing the banner
        const shouldNotShowBanner =
            this.themeClass === 'theme-mykempower' || !this.isOrganizationAdmin || this.bannerWasShownToday();

        if (shouldNotShowBanner) {
            return;
        }

        try {
            // Fetch all licenses including future ones
            const response = await this.licensingApi.getActiveLicenses({ includeFuture: true });
            const allLicenses = response?.licenses || [];

            // Filter to ChargEye subscriptions (non-MyKempower), sorted by start date
            const chargeEyeLicenses = allLicenses
                .filter((l) => !l.type.startsWith('mykempower'))
                .sort((a, b) => new Date(a.startDate).getTime() - new Date(b.startDate).getTime());

            if (chargeEyeLicenses.length === 0) {
                return;
            }

            // Find currently active license
            // Note: This will return undefined if all licenses start in the future
            const today = new Date();
            today.setUTCHours(0, 0, 0, 0);
            const todayTime = today.getTime();

            const activeLicense = chargeEyeLicenses.find((l) => {
                const startDate = new Date(l.startDate);
                startDate.setUTCHours(0, 0, 0, 0);
                const endDate = l.endDate ? new Date(l.endDate) : null;
                if (endDate) {
                    endDate.setUTCHours(23, 59, 59, 999); // End of day
                }

                const hasStarted = startDate.getTime() <= todayTime;
                const hasNotEnded = !endDate || endDate.getTime() >= todayTime;

                return hasStarted && hasNotEnded;
            });

            if (!activeLicense || !activeLicense.endDate) {
                return; // No currently active license or license is permanent
            }

            // Calculate days until expiry
            const endDate = new Date(activeLicense.endDate);
            endDate.setUTCHours(0, 0, 0, 0);

            const diffTime = endDate.getTime() - today.getTime();
            const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

            // Check if within threshold (30 days)
            if (diffDays < 0 || diffDays > 30) {
                return;
            }

            // Check if there's a following subscription
            const followingLicense = chargeEyeLicenses.find((l) => {
                const startDate = new Date(l.startDate);
                startDate.setUTCHours(0, 0, 0, 0);
                return startDate.getTime() > endDate.getTime();
            });

            if (!followingLicense) {
                // No following subscription - show warning
                this.daysUntilSubscriptionExpiry = diffDays;
                this.showBanner = true;
                this.cdRef.markForCheck();
                return;
            }

            // Check gap between current and following subscription
            const nextStartDate = new Date(followingLicense.startDate);
            nextStartDate.setUTCHours(0, 0, 0, 0);

            // Calculate the day after current subscription ends
            const dayAfterEnd = new Date(endDate);
            dayAfterEnd.setUTCDate(dayAfterEnd.getUTCDate() + 1);
            dayAfterEnd.setUTCHours(0, 0, 0, 0);

            // Check if gap is more than 0 days
            const gapDays = Math.floor((nextStartDate.getTime() - dayAfterEnd.getTime()) / (1000 * 60 * 60 * 24));

            if (gapDays > 0) {
                // Gap exists - show warning
                this.daysUntilSubscriptionExpiry = diffDays;
                this.showBanner = true;
            }

            this.cdRef.markForCheck();
        } catch (error) {
            console.error('Error checking subscription expiry:', error);
        }
    }

    private refreshButtons(helper: PermissionsHelper): void {
        this.filteredButtons = [];

        this.hasPermissionsToSearch$.next(helper?.hasAccess('fleet.chargingStation.overview.globalSearch'));

        for (const b of navigationButtons) {
            // check duplicate links
            const linkExisted = this.filteredButtons.some((_b) => _b.routerLink === b.routerLink);
            if (linkExisted) {
                continue;
            }

            // tenant id filter
            if (b.tenantIds && this.auth.profile?.tenantId && !b.tenantIds.includes(this.auth.profile.tenantId)) {
                // don't add this button, for a different tenant id
            }

            // check access
            if (b.access) {
                const condition: AccessCondition = b.accessCondition || 'AND';
                const accessString: AccessString = b.access;

                if (!helper?.hasAccess(accessString, condition)) {
                    continue;
                }
            }

            // feature flag filter
            if (b.featureFlag && !this.featureFlagState[b.featureFlag]) {
                continue;
            }

            // sys admin filter - override access filter
            if (b.sysAdminOnly && !this.auth.getPermissions().isSystemAdmin) {
                continue;
            }

            this.filteredButtons.push(b);
        }

        this.cdRef.markForCheck();
    }
}
